'use client'

import { useRef } from 'react'
import { Text, Note, Spacer, Chip, Icon } from '@vinted/web-ui'
import { ArrowRight12 } from '@vinted/monochrome-icons'

import { InView } from 'react-intersection-observer'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { viewSuggestedMessageEvent } from '@marketplace-web/messaging/tracking-data'

type Props = {
  messages: ReadonlyArray<{ id: number; text: string }>
  onMessageClick: (text: string) => void
  trackingContext?: {
    transactionId: number | null | undefined
    itemIds: string | null | undefined
  }
}

const SuggestedMessages = ({ messages, trackingContext, onMessageClick }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.suggested_messages')

  const seenSuggestedMessagesIds = useRef<Set<number>>(new Set())

  const handleMessageClick = (id: number, text: string, position: number) => () => {
    track(viewSuggestedMessageEvent({ ...trackingContext, id: id.toString(), position }))

    onMessageClick(text)
  }

  const handleSuggestedMessageVisibility = (id: number, position: number) => (inView: boolean) => {
    if (!inView) return
    if (seenSuggestedMessagesIds.current.has(id)) return

    seenSuggestedMessagesIds.current.add(id)

    track(viewSuggestedMessageEvent({ ...trackingContext, id: id.toString(), position }))
  }

  return (
    <>
      <Note
        text={<Text as="h4" html text={translate('note')} type="caption" alignment="center" />}
        alignment="center"
        styling="wide"
        inverse
      />
      <SeparatedList separator={<Spacer />}>
        {messages.map(({ id, text }, index) => (
          <InView
            className="u-flexbox u-justify-content-flex-end conversation-message-container"
            onChange={handleSuggestedMessageVisibility(id, index + 1)}
            key={id}
          >
            <Chip
              radius="round"
              text={text}
              textType="subtitle"
              suffix={<Icon name={ArrowRight12} />}
              onClick={handleMessageClick(id, text, index + 1)}
            />
          </InView>
        ))}
      </SeparatedList>
    </>
  )
}

export default SuggestedMessages
